import { apiService } from '@/services/api'
import { forgetSignupFirst } from '@/hooks/useSignupFirst'

const URL_PATTERN = /^(https?:\/\/)?([a-z0-9-]+(\.[a-z0-9-]+)+)(\/[\w\-./?%&=#]*)?$/i

export function normalizeOnboardingUrl(raw: string): string | null {
  const trimmed = raw.trim()
  if (!URL_PATTERN.test(trimmed)) return null
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

export function getClaimParams(searchParams: URLSearchParams) {
  const sessionId = searchParams.get('claimSession') || ''
  const token = searchParams.get('claimToken') || ''
  const domain = searchParams.get('domain') || ''
  return { sessionId, token, domain }
}

export function buildClaimSearch({
  sessionId,
  token,
  domain,
}: {
  sessionId: string
  token: string
  domain?: string
}) {
  const params = new URLSearchParams({
    claimSession: sessionId,
    claimToken: token,
  })
  if (domain) params.set('domain', domain)
  return params.toString()
}

export function buildClaimRedirect(searchParams: URLSearchParams, fallback: string) {
  const { sessionId, token, domain } = getClaimParams(searchParams)
  if (!sessionId || !token) return fallback
  return `/onboarding/claim?${buildClaimSearch({ sessionId, token, domain })}`
}

export function getMarketingUrlParam(searchParams: URLSearchParams) {
  return (searchParams.get('url') || '').trim()
}

export function buildSignUpUrlWithUrl(rawUrl: string, basePath = '/sign-up') {
  const trimmed = rawUrl.trim()
  if (!trimmed) return basePath

  const params = new URLSearchParams({ url: trimmed })
  return `${basePath}?${params.toString()}`
}

export function buildPublicScanUrl(sessionId: string, token: string) {
  const params = new URLSearchParams({ t: token })
  return `/scan/${encodeURIComponent(sessionId)}?${params.toString()}`
}

export function preloadPublicScanPage() {
  return import('@/pages/PublicScanPage').catch(() => undefined)
}

/* Where a visitor goes when a scan needs an account first. */
const SIGN_UP_PATH = '/sign-up'

export async function startPublicSiteScan(
  rawUrl: string,
  navigate: (path: string) => void = (path) => {
    window.location.href = path
  }
) {
  const normalizedUrl = normalizeOnboardingUrl(rawUrl)
  if (!normalizedUrl) {
    throw new Error('Enter a valid website URL')
  }

  try {
    /*
      Wait for the scan page's chunk before navigating to it.

      This preload used to be fired with `void`, so navigation happened as soon
      as the API answered whether or not the chunk had arrived. PublicScanPage
      is lazy, so React then suspended on the new route, and React 18 holds the
      PREVIOUS screen up while a route suspends rather than dropping straight
      to a fallback. The result was the homepage, graphic and all, sitting over
      the scan that had already started, for about as long as the chunk took.

      Both run in parallel and the API is reliably the slower of the two, so on
      any normal connection this costs nothing: by the time the scan id comes
      back the chunk is already in memory.

      Capped, because correctness here must not become a hostage. If the chunk
      is slow or fails outright the scan has still started and the person must
      be taken to it; they get the fallback they used to get, which is the same
      prep screen either way.
    */
    const chunkReady = preloadPublicScanPage()
    const response = await apiService.startOnboardingScan(normalizedUrl)
    await Promise.race([chunkReady, new Promise(resolve => setTimeout(resolve, 1500))])
    navigate(buildPublicScanUrl(response.scan.sessionId, response.scan.token))
  } catch (error: any) {
    const responseData = error?.response?.data
    /*
      Not a failure. The address box is drawn only while this tab believes
      sign-up first is off, and the server has just said it is on: the switch
      was turned while the tab was open. The tab's answer is stale, so it is
      forgotten, and the visitor goes where the server said to go. Nothing is
      thrown, so the box shows no error on the way out.
    */
    if (error?.response?.status === 401 && responseData?.error === 'SIGN_UP_REQUIRED') {
      forgetSignupFirst()
      navigate(SIGN_UP_PATH)
      return
    }
    const message =
      (typeof responseData === 'string' ? responseData : responseData?.message || responseData?.error) ||
      error?.message ||
      'We could not start the scan. Please try again.'
    throw new Error(message)
  }
}
